import React, { useRef } from 'react';
import { Music, UploadCloud, Volume2, VolumeX, Repeat, Trash2, Clock } from 'lucide-react';
import { AudioTrack, formatTime } from '../../types/editor';

interface Props {
  audioTracks: AudioTrack[];
  onImportAudio: (files: FileList) => void;
  onUpdateTrack: (id: string, updates: Partial<AudioTrack>) => void;
  onDeleteTrack: (id: string) => void;
}

export default function AudioPanel({ audioTracks, onImportAudio, onUpdateTrack, onDeleteTrack }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%', gap: 8,
    }}>
      {/* Upload area */}
      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: '2px dashed rgba(34,197,94,0.3)',
          borderRadius: 10, padding: '16px 12px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          cursor: 'pointer', transition: 'all 0.2s',
          background: 'rgba(34,197,94,0.04)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(34,197,94,0.6)'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(34,197,94,0.3)'}
      >
        <UploadCloud size={20} style={{ color: '#4ade80' }} />
        <span style={{ fontSize: '0.72rem', color: '#4ade80', fontWeight: 500 }}>
          Importar Trilha
        </span>
        <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)' }}>
          MP3, WAV, OGG
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mp3,audio/wav,audio/ogg,audio/mpeg"
          multiple
          onChange={(e) => e.target.files && onImportAudio(e.target.files)}
          style={{ display: 'none' }}
        />
      </div>

      {/* Track list */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}
        className="right-panel-scroll"
      >
        {audioTracks.length === 0 && (
          <div style={{
            padding: 20, textAlign: 'center', fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.25)',
          }}>
            Nenhuma trilha adicionada
          </div>
        )}

        {audioTracks.map(track => (
          <AudioTrackCard
            key={track.id}
            track={track}
            onUpdate={(updates) => onUpdateTrack(track.id, updates)}
            onDelete={() => onDeleteTrack(track.id)}
          />
        ))}
      </div>
    </div>
  );
}

function AudioTrackCard({
  track, onUpdate, onDelete,
}: {
  track: AudioTrack;
  onUpdate: (updates: Partial<AudioTrack>) => void;
  onDelete: () => void;
}) {
  return (
    <div style={{
      background: 'rgba(34,197,94,0.04)', borderRadius: 8,
      border: '1px solid rgba(34,197,94,0.12)', padding: '8px 10px',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Music size={12} style={{ color: track.muted ? '#f87171' : '#4ade80', flexShrink: 0 }} />
        <span style={{
          fontSize: '0.68rem', color: 'rgba(255,255,255,0.7)', fontWeight: 500,
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {track.name}
        </span>
        <span style={{
          fontSize: '0.52rem', fontWeight: 600, padding: '1px 5px', borderRadius: 4,
          background: (track.kind || 'voice') === 'voice' ? 'rgba(251,191,36,0.15)' : 'rgba(34,211,238,0.15)',
          color: (track.kind || 'voice') === 'voice' ? '#fbbf24' : '#67e8f9',
        }}>
          {(track.kind || 'voice') === 'voice' ? 'Voz' : track.kind === 'import' ? 'Import' : 'Som'}
        </span>
        <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Clock size={8} /> {formatTime(track.duration)}
        </span>
        <button
          onClick={onDelete}
          style={{
            background: 'none', border: 'none', color: '#f87171',
            cursor: 'pointer', padding: 2, display: 'flex',
          }}
        >
          <Trash2 size={11} />
        </button>
      </div>

      {/* Volume + mute */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          title={track.muted ? 'Ativar trilha' : 'Mutar trilha'}
          onClick={() => onUpdate({ muted: !track.muted })}
          style={{
            background: track.muted ? 'rgba(239,68,68,0.12)' : 'none',
            border: `1px solid ${track.muted ? 'rgba(239,68,68,0.35)' : 'transparent'}`,
            borderRadius: 4, padding: 2, display: 'flex', cursor: 'pointer',
            color: track.muted ? '#f87171' : 'rgba(255,255,255,0.4)',
          }}
        >
          {track.muted ? <VolumeX size={11} /> : <Volume2 size={11} />}
        </button>
        <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)', width: 40 }}>
          {track.muted ? 'off' : `${Math.round(track.volume * 100)}%`}
        </span>
        <input
          type="range" min={0} max={1} step={0.05}
          value={track.muted ? 0 : track.volume}
          onChange={(e) => onUpdate({ volume: parseFloat(e.target.value), muted: false })}
          style={{ flex: 1, accentColor: '#22c55e', height: 3 }}
        />
      </div>

      {/* Offset */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Clock size={11} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
        <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)', width: 40 }}>
          Início:
        </span>
        <input
          type="number" min={0} step={0.5}
          value={track.startOffset}
          onChange={(e) => onUpdate({ startOffset: Math.max(0, parseFloat(e.target.value) || 0) })}
          style={{
            width: 55, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 4, padding: '2px 5px', color: '#fff', fontSize: '0.6rem',
            fontFamily: 'monospace',
          }}
        />
        <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)' }}>seg</span>
      </div>

      {/* Loop toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Repeat size={11} style={{ color: track.loop ? '#4ade80' : 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
        <button
          onClick={() => onUpdate({ loop: !track.loop })}
          style={{
            background: track.loop ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${track.loop ? 'rgba(34,197,94,0.4)' : 'rgba(255,255,255,0.08)'}`,
            borderRadius: 4, padding: '2px 8px',
            fontSize: '0.58rem', color: track.loop ? '#4ade80' : 'rgba(255,255,255,0.4)',
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {track.loop ? 'Loop Ativo' : 'Loop Desativado'}
        </button>
      </div>
    </div>
  );
}
